import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaApple, FaAndroid, FaGlobe, FaCopy } from 'react-icons/fa';
import { getBaseUrl } from '../utils/tokenUtils';

const SupportTicketHowTo = ({ isOpen, onClose, projects, supportModules, onShowToast }) => {
  const [selectedProjectForHowTo, setSelectedProjectForHowTo] = useState('');

  const getSupportModulesForProject = () => {
    if (!selectedProjectForHowTo) return [];
    return supportModules.filter(module => module.projectId?._id === selectedProjectForHowTo);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      if (onShowToast) {
        onShowToast(`${label} copied to clipboard!`, 'success');
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      if (onShowToast) {
        onShowToast('Failed to copy to clipboard', 'error');
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white flex justify-between items-center mb-6">
                  Support Ticket API Integration
                  <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <FaTimes />
                  </button>
                </DialogTitle>

                <div className="space-y-6">
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-md font-medium text-blue-300 mb-2">📱 Public API Integration</h4>
                    <p className="text-sm text-gray-300">Your users can create support tickets directly from your mobile app or website using this public API endpoint.</p>
                  </div>

                  {/* Project Selection */}
                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Select Project</h4>
                    {projects.length === 0 ? (
                      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                        <h5 className="text-red-300 font-medium mb-2">⚠️ No Projects Found</h5>
                        <p className="text-sm text-gray-300 mb-3">
                          You need to create a project first before setting up support tickets.
                        </p>
                        <button
                          onClick={() => {
                            onClose();
                            // Navigate to projects page - you might need to pass a navigation function as prop
                            window.location.href = '/dashboard/projects';
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          Go to Projects
                        </button>
                      </div>
                    ) : (
                      <select
                        value={selectedProjectForHowTo}
                        onChange={(e) => setSelectedProjectForHowTo(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-4"
                      >
                        <option value="">Choose a project...</option>
                        {projects.map(project => (
                          <option key={project._id} value={project._id}>{project.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {selectedProjectForHowTo ? (
                    getSupportModulesForProject().length > 0 ? (
                      <div>
                        <h4 className="text-md font-medium text-white mb-3">Your Support Modules</h4>
                        <div className="space-y-3 mb-6">
                        {getSupportModulesForProject().map((module) => (
                          <div key={module._id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-white font-medium">{module.name}</h5>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-400">Module ID:</span>
                                <button
                                  onClick={() => copyToClipboard(module._id, 'Module ID')}
                                  className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition-colors cursor-pointer"
                                  title="Click to copy Module ID"
                                >
                                  {module._id}
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-gray-300 mb-3">
                              Project: {module.projectId?.name || 'Unknown Project'}
                            </p>
                            
                            {module.projectId?.products && module.projectId.products.length > 0 ? (
                              <div>
                                <h6 className="text-sm font-medium text-gray-300 mb-2">Available Products (use productId in API):</h6>
                                
                                {/* Table Header */}
                                <div className="grid grid-cols-12 gap-2 mb-2 px-3 py-2 bg-gray-800 rounded-lg text-xs font-medium text-gray-400">
                                  <div className="col-span-1">Platform</div>
                                  <div className="col-span-7">Product Name</div>
                                  <div className="col-span-4">Product ID</div>
                                </div>
                                
                                {/* Table Body */}
                                <div className="space-y-1">
                                  {module.projectId.products.map((product) => (
                                    <div key={product._id} className="grid grid-cols-12 gap-2 items-center bg-gray-700 rounded-lg p-3">
                                      {/* Platform Icon */}
                                      <div className="col-span-1 flex justify-center">
                                        {product.platform === 'ios' ? (
                                          <FaApple className="w-4 h-4 text-gray-300" />
                                        ) : product.platform === 'android' ? (
                                          <FaAndroid className="w-4 h-4 text-green-400" />
                                        ) : (
                                          <FaGlobe className="w-4 h-4 text-blue-400" />
                                        )}
                                      </div>
                                      
                                      {/* Product Name */}
                                      <div className="col-span-7">
                                        <span className="text-sm text-white font-medium">{product.name || 'Unnamed Product'}</span>
                                        <span className="text-xs text-gray-400 capitalize ml-2">({product.platform})</span>
                                      </div>
                                      
                                      {/* Product ID */}
                                      <div className="col-span-4">
                                        <button
                                          onClick={() => copyToClipboard(product._id, 'Product ID')}
                                          className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded hover:bg-gray-500 transition-colors cursor-pointer w-full text-left truncate"
                                          title="Click to copy Product ID"
                                        >
                                          {product._id}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">No products available for this project</p>
                            )}
                          </div>
                        ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                        <h5 className="text-red-300 font-medium mb-2">⚠️ No Support Modules Found</h5>
                        <p className="text-sm text-gray-300 mb-3">
                          This project doesn't have any support modules yet. You need to create a support module first.
                        </p>
                        <button
                          onClick={() => {
                            onClose();
                            // Navigate to support modules page
                            window.location.href = '/dashboard/support';
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          Go to Support Modules
                        </button>
                      </div>
                    )
                  ) : null}

                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Create Support Ticket (Public API)</h4>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-300 mb-2">HTTP Method: <span className="text-green-400">POST</span></p>
                      <p className="text-sm text-gray-300 mb-2">URL: <span className="text-blue-400">{getBaseUrl()}/api/support/support-tickets</span></p>
                      <p className="text-sm text-gray-300 mb-2">Headers: <span className="text-yellow-400">Content-Type: application/json</span></p>
                      <div className="mt-3">
                        <p className="text-sm text-gray-300 mb-2">Request Body:</p>
                        <pre className="bg-black rounded p-3 text-sm text-gray-300 overflow-x-auto">
{`{
  "supportModuleId": "YOUR_SUPPORT_MODULE_ID",
  "productId": "YOUR_PRODUCT_ID",
  "subject": "App crashes on startup",
  "description": "The app crashes immediately when I try to open it",
  "category": "Bug Report",
  "priority": "high",
  "language": "en",
  "customerInfo": {
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+1234567890"
  },
  "customData": {
    "appVersion": "1.2.3",
    "deviceModel": "iPhone 14",
    "osVersion": "iOS 16.0",
    "userId": "user123",
    "sessionId": "session456",
    "buildNumber": "123",
    "deviceId": "ABC123",
    "networkType": "wifi"
  }
}`}
                        </pre>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Required Fields</h4>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-red-400 font-medium mb-2">Required:</p>
                          <ul className="text-gray-300 space-y-1">
                            <li>• <span className="text-white">supportModuleId</span> - Your support module ID</li>
                            <li>• <span className="text-white">productId</span> - Your product ID from dashboard</li>
                            <li>• <span className="text-white">subject</span> - Ticket subject</li>
                            <li>• <span className="text-white">description</span> - Detailed description</li>
                            <li>• <span className="text-white">language</span> - Language code (e.g., "en", "tr")</li>
                          </ul>
                        </div>
                        <div>
                          <p className="text-green-400 font-medium mb-2">Optional:</p>
                          <ul className="text-gray-300 space-y-1">
                            <li>• <span className="text-white">category</span> - Ticket category</li>
                            <li>• <span className="text-white">priority</span> - "low", "medium", "high", "urgent"</li>
                            <li>• <span className="text-white">customerInfo.name</span> - Customer name</li>
                            <li>• <span className="text-white">customerInfo.email</span> - Customer email</li>
                            <li>• <span className="text-white">customerInfo.phone</span> - Customer phone</li>
                            <li>• <span className="text-white">customData</span> - Any additional data (displayed in ticket details)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-white mb-3">Response Example</h4>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <pre className="bg-black rounded p-3 text-sm text-gray-300 overflow-x-auto">
{`{
  "success": true,
  "ticket": {
    "id": "TICKET_ID",
    "ticketNumber": "ST-2024-001",
    "status": "open",
    "createdAt": "2024-01-15T10:30:00Z"
  },
  "message": "Support ticket created successfully"
}`}
                      </pre>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SupportTicketHowTo;
